// Admin tab to manage public newsletter subscribers.
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";

export default function NewsletterTab({ locale = "fr" }) {
  const isFr = locale === "fr";
  const [data, setData] = useState({ items: [], total: 0, active: 0 });
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    const r = await api.get("/admin/newsletter/subscribers");
    setData(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (sid, email) => {
    if (!window.confirm(isFr ? `Supprimer définitivement ${email} ?` : `Permanently delete ${email}?`)) return;
    await api.delete(`/admin/newsletter/subscribers/${sid}`);
    load();
  };

  const q = search.toLowerCase();
  const filtered = data.items.filter((it) =>
    !q || (it.email || "").toLowerCase().includes(q) || (it.name || "").toLowerCase().includes(q),
  );
  const unsubscribed = data.total - data.active;

  return (
    <div className="space-y-6" data-testid="newsletter-tab">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-display text-2xl">{isFr ? "Newsletter" : "Newsletter"}</h2>
        <a className="ml-auto btn btn-outline btn-sm"
           data-testid="newsletter-export-csv"
           href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/newsletter/export.csv`}
           target="_blank" rel="noreferrer">
          📊 {isFr ? "Export CSV" : "Export CSV"}
        </a>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <Kpi label={isFr ? "Total" : "Total"} value={data.total} color="var(--gold)" testId="nl-kpi-total" />
        <Kpi label={isFr ? "Actifs" : "Active"} value={data.active} color="var(--moss)" testId="nl-kpi-active" />
        <Kpi label={isFr ? "Désinscrits" : "Unsubscribed"} value={unsubscribed} color="var(--muted)" testId="nl-kpi-unsub" />
      </div>

      <input className="input-classic w-full"
             placeholder={isFr ? "Rechercher email ou nom…" : "Search email or name…"}
             value={search} onChange={(e) => setSearch(e.target.value)}
             data-testid="newsletter-search" />

      <div className="admin-card overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ borderBottom: "1px solid var(--border)" }}>
              {[isFr ? "Email" : "Email", isFr ? "Nom" : "Name",
                isFr ? "Langue" : "Lang", isFr ? "Inscrit le" : "Subscribed",
                isFr ? "Statut" : "Status", ""].map((h, i) => (
                <th key={i} className="text-left p-2 font-caps text-[0.6rem]"
                    style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((it, i) => (
              <tr key={it.id || i} style={{ borderBottom: "1px solid var(--border)" }}
                  data-testid={`nl-row-${i}`}>
                <td className="p-2">{it.email}</td>
                <td className="p-2 text-xs" style={{ color: "var(--ink-2)" }}>{it.name || "—"}</td>
                <td className="p-2 text-xs">{it.locale}</td>
                <td className="p-2 text-xs" style={{ color: "var(--muted)" }}>
                  {(it.subscribed_at || "").slice(0, 10)}
                </td>
                <td className="p-2">
                  {it.unsubscribed_at ? (
                    <span className="px-2 py-0.5 text-[0.65rem] font-caps"
                          style={{ backgroundColor: "var(--surface-2)", color: "var(--muted)", letterSpacing: "0.15em" }}>
                      {isFr ? "Désinscrit" : "Unsubscribed"}
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 text-[0.65rem] font-caps"
                          style={{ backgroundColor: "rgba(83,128,72,0.16)", color: "var(--moss)", letterSpacing: "0.15em" }}>
                      ✓ {isFr ? "Actif" : "Active"}
                    </span>
                  )}
                </td>
                <td className="p-2 text-right">
                  <button className="btn btn-outline btn-sm" onClick={() => remove(it.id, it.email)}
                          data-testid={`nl-delete-${i}`}>✕</button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan="6" className="p-6 text-center" style={{ color: "var(--muted)" }}>
                {isFr ? "Aucun inscrit" : "No subscribers"}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ label, value, color, testId }) {
  return (
    <div className="admin-card text-center" data-testid={testId}>
      <div className="font-caps text-[0.6rem]" style={{ color: "var(--muted)", letterSpacing: "0.18em" }}>{label}</div>
      <div className="font-display text-3xl" style={{ color }}>{value}</div>
    </div>
  );
}
